import React, { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useScanStatus } from '../hooks/useScanStatus';

/**
 * T030: ScanContext provider for global scan state management.
 * Tracks the currently active scan job so the status pill can display on all pages.
 * T049: Fires shopify.toast.show on scan completion.
 */

interface ScanContextValue {
  /** The active scan job ID, or null if no scan is running */
  activeJobId: string | null;
  /** Set the active scan job ID */
  setActiveJobId: (jobId: string | null) => void;
  /** Whether a scan is currently active */
  isScanning: boolean;
  /** Clear the active scan */
  clearScan: () => void;
  /** The job ID that just completed (for notification banner) */
  completedJobId: string | null;
  /** Dismiss the completed notification */
  dismissCompleted: () => void;
}

const ScanContext = createContext<ScanContextValue | undefined>(undefined);

interface ScanProviderProps {
  children: React.ReactNode;
}

export function ScanProvider({ children }: Readonly<ScanProviderProps>) {
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [completedJobId, setCompletedJobId] = useState<string | null>(null);
  const prevStatusRef = useRef<string | null>(null);

  // Poll scan status for the active job
  const { data: scanProgress } = useScanStatus(activeJobId);

  // T049: Fire toast and track completion when scan transitions to COMPLETED
  useEffect(() => {
    const currentStatus = scanProgress?.status ?? null;
    const prevStatus = prevStatusRef.current;

    if (
      currentStatus === 'COMPLETED' &&
      prevStatus !== null &&
      prevStatus !== 'COMPLETED'
    ) {
      // Fire Shopify toast
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const shopify = (globalThis as any).shopify;
      if (typeof shopify?.toast?.show === 'function') {
        shopify.toast.show('Scan complete — View results');
      }

      // Track completed job for notification banner
      setCompletedJobId(activeJobId);
      setActiveJobId(null);
    }

    prevStatusRef.current = currentStatus;
  }, [scanProgress?.status, activeJobId]);

  const clearScan = useCallback(() => {
    setActiveJobId(null);
  }, []);

  const dismissCompleted = useCallback(() => {
    setCompletedJobId(null);
  }, []);

  const value = useMemo<ScanContextValue>(
    () => ({
      activeJobId,
      setActiveJobId,
      isScanning: activeJobId !== null,
      clearScan,
      completedJobId,
      dismissCompleted,
    }),
    [activeJobId, clearScan, completedJobId, dismissCompleted],
  );

  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
}

export function useScanContext(): ScanContextValue {
  const context = useContext(ScanContext);
  if (!context) {
    throw new Error('useScanContext must be used within a ScanProvider');
  }
  return context;
}
